import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import CustomSelect from "./ui/CustomSelect";

const UpdatePlan = ({ plan }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    plan_name: "",
    plan_type: "",
    customer_segment: "",
    base_price: "",
    per_vehicle_fee: "",
    volume_threshold: "",
    min_vehicle_count: "",
    max_vehicle_count: "",
    max_vehicles_allowed: "",
    is_trial_plan: false,
    description: "",
  });

  // Options for dropdowns
  const planTypeOptions = [
    { label: "Tiered", value: "tiered" },
    { label: "Volume", value: "volume" },
    { label: "Flat", value: "flat" },
  ];

  const customerSegmentOptions = [
    { label: "Large", value: "Large" },
    { label: "Medium", value: "Medium" },
    { label: "Small", value: "Small" },
  ];

  const trialPlanOptions = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  useEffect(() => {
    if (plan) {
      setFormData({
        ...plan,
        base_price: plan.base_price ?? "",
        per_vehicle_fee: plan.per_vehicle_fee ?? "",
        volume_threshold: plan.volume_threshold ?? "",
        min_vehicle_count: plan.min_vehicle_count ?? "",
        max_vehicle_count: plan.max_vehicle_count ?? "",
        max_vehicles_allowed: plan.max_vehicles_allowed ?? "",
        is_trial_plan: !!plan.is_trial_plan,
        description: plan.description ?? "",
      });
    }
  }, [plan]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "number" && parseFloat(value) < 0) return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption?.value || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare payload with parsed values
      const payload = {
        ...formData,
        base_price: parseFloat(formData.base_price),
        per_vehicle_fee: parseFloat(formData.per_vehicle_fee),
        volume_threshold: parseInt(formData.volume_threshold, 10),
        min_vehicle_count: parseInt(formData.min_vehicle_count, 10),
        max_vehicle_count: parseInt(formData.max_vehicle_count, 10),
        max_vehicles_allowed: parseInt(formData.max_vehicles_allowed, 10),
      };

      // Use apiCall helper with PUT method
      const response = await apiCall(
        "put",
        `/update-pricing-plan/${plan?.plan_id}`,
        payload
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "PUT");
        navigate("/all-plans");
      } else {
        handleCustomResponse("error", "Failed to update plan.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ overflow: 'visible' }}>
      <div className="card h-100 p-0 radius-12" style={{ overflow: 'visible' }}>
        <div className="card-body p-40" style={{ overflow: 'visible' }}>
          <form action="#" autoComplete="off" onSubmit={handleSubmit}>
            <div className="row">
              {/* Plan Name */}
              <div className="col-sm-6">
                <div className="mb-20">
                  <label
                    htmlFor="plan_name"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Plan Name <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    id="plan_name"
                    name="plan_name"
                    value={formData.plan_name}
                    onChange={handleChange}
                    placeholder="Enter Plan Name"
                    required
                  />
                </div>
              </div>

              {/* Plan Type */}
              <div className="col-sm-6">
                <div className="mb-20" style={{ position: 'relative', zIndex: 1000 }}>
                  <label
                    htmlFor="plan_type"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Plan Type <span className="text-danger-600">*</span>
                  </label>
                  <CustomSelect
                    name="plan_type"
                    value={formData.plan_type}
                    onChange={(selectedOption) => 
                      handleSelectChange("plan_type", selectedOption)
                    }
                    options={planTypeOptions}
                    placeholder="Select Plan Type"
                    labelKey="label"
                    valueKey="value"
                    isClearable={false}
                    menuPlacement="auto"
                  />
                </div>
              </div>

              {/* Customer Segment */}
              <div className="col-sm-6">
                <div className="mb-20" style={{ position: 'relative', zIndex: 999 }}>
                  <label
                    htmlFor="customer_segment"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Customer Segment <span className="text-danger-600">*</span>
                  </label>
                  <CustomSelect
                    name="customer_segment"
                    value={formData.customer_segment}
                    onChange={(selectedOption) => 
                      handleSelectChange("customer_segment", selectedOption)
                    }
                    options={customerSegmentOptions}
                    placeholder="Select Customer Segment"
                    labelKey="label"
                    valueKey="value"
                    isClearable={false}
                    menuPlacement="auto"
                  />
                </div>
              </div>

              {/* Base Price */}
              <div className="col-sm-6">
                <div className="mb-20">
                  <label
                    htmlFor="base_price"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Base Price <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control radius-8"
                    id="base_price"
                    name="base_price"
                    value={formData.base_price}
                    onChange={handleChange}
                    placeholder="Enter Base Price"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Per Vehicle Fee */}
              <div className="col-sm-6">
                <div className="mb-20">
                  <label
                    htmlFor="per_vehicle_fee"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Per Vehicle Fee
                  </label>
                  <input
                    type="number"
                    className="form-control radius-8"
                    id="per_vehicle_fee"
                    name="per_vehicle_fee"
                    value={formData.per_vehicle_fee}
                    onChange={handleChange}
                    placeholder="Enter Per Vehicle Fee"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Volume Threshold */}
              <div className="col-sm-6">
                <div className="mb-20">
                  <label
                    htmlFor="volume_threshold"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Volume Threshold
                  </label>
                  <input
                    type="number"
                    className="form-control radius-8"
                    id="volume_threshold"
                    name="volume_threshold"
                    value={formData.volume_threshold}
                    onChange={handleChange}
                    placeholder="Enter Volume Threshold"
                    min="0"
                  />
                </div>
              </div>

              {/* Min Vehicle Count */}
              <div className="col-sm-6">
                <div className="mb-20">
                  <label
                    htmlFor="min_vehicle_count"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Min Vehicle Count
                  </label>
                  <input
                    type="number"
                    className="form-control radius-8"
                    id="min_vehicle_count"
                    name="min_vehicle_count"
                    value={formData.min_vehicle_count}
                    onChange={handleChange}
                    placeholder="Enter Min Vehicle Count"
                    min="0"
                  />
                </div>
              </div>

              {/* Max Vehicle Count */}
              <div className="col-sm-6">
                <div className="mb-20">
                  <label
                    htmlFor="max_vehicle_count"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Max Vehicle Count
                  </label>
                  <input
                    type="number"
                    className="form-control radius-8"
                    id="max_vehicle_count"
                    name="max_vehicle_count"
                    value={formData.max_vehicle_count}
                    onChange={handleChange}
                    placeholder="Enter Max Vehicle Count"
                    min="0"
                  />
                </div>
              </div>

              {/* Max Vehicles Allowed */}
              <div className="col-sm-6">
                <div className="mb-20">
                  <label
                    htmlFor="max_vehicles_allowed"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Max Vehicles Allowed
                  </label>
                  <input
                    type="number"
                    className="form-control radius-8"
                    id="max_vehicles_allowed"
                    name="max_vehicles_allowed"
                    value={formData.max_vehicles_allowed}
                    onChange={handleChange}
                    placeholder="Enter Max Vehicles Allowed"
                    min="0"
                  />
                </div>
              </div>

              {/* Is Trial Plan */}
              <div className="col-sm-6">
                <div className="mb-20" style={{ position: 'relative', zIndex: 998 }}>
                  <label
                    htmlFor="is_trial_plan"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Is Trial Plan? <span className="text-danger-600">*</span>
                  </label>
                  <CustomSelect
                    name="is_trial_plan"
                    value={formData.is_trial_plan}
                    onChange={(selectedOption) => 
                      handleSelectChange("is_trial_plan", selectedOption)
                    }
                    options={trialPlanOptions}
                    placeholder="Select Option"
                    labelKey="label"
                    valueKey="value"
                    isClearable={false}
                    menuPlacement="auto"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="col-sm-12">
                <div className="mb-20">
                  <label
                    htmlFor="description"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Description
                  </label>
                  <textarea
                    className="form-control radius-8"
                    id="description"
                    name="description"
                    rows="4"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description (optional)"
                  ></textarea>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <Link
                    to="/all-plans"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8 text-decoration-none"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary-600 border border-primary-600 text-md px-24 py-12 radius-8"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update Plan"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePlan;